/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ADDON_KIND } from '@app/constants';

import {
  Address,
  AvailablePhoneNumber,
  DirectoryListType,
  GetCurrentAddOnsPayload,
  GetScrubbedAddressPayload,
  ProductType,
} from '@mfe/shared/schema-types';

import { ErrorsObj, RuntimeError } from '../graphql';
import { ScrubAddressModalType } from '../scrubbedAddress';
import { AddonManagementModalType } from './utils';
import {
  ScrubAddressProcessStatus,
  ScrubAddressRecommendation,
} from '../../../../platform/src/server/dataSources/PAL/types';
import { EicMessage } from '../types';

export enum AddOnType {
  Voice = 'Voice',
  EasyCare = 'EasyCare',
  CyberProtect = 'CyberProtect',
  StaticIP = 'StaticIP',
}

export type ScrubbedE911Address = {
  rawAddress?: Omit<GetScrubbedAddressPayload, 'coordinates' | 'country' | 'processStatus' | 'recommendation'> | null;
  formattedAddress: {
    formattedAddressLine1: string;
    formattedAddressLine2: string;
  } | null;
  processStatus?: ScrubAddressProcessStatus;
  recommendation?: ScrubAddressRecommendation;
};

type EmergencyAddress = {
  streetAddress: string; // should be line 1 of addressLines
  aptUnit?: string; // should be line 2 of addressLines
  cityAddress: string; // municipality
  stateProvince: string; // region
  zipCode: string; // postalCode
};

export type VoiceConfig = {
  postalCode: string;
  selectedPhoneNumber: string;
  emergencyAddress: EmergencyAddress;
  useServiceAddressForE911: boolean;
  callerId: string;
  blockCallerId: boolean;
  directoryListingType: DirectoryListType | null;
  publishDirectoryListAddress: boolean;
  showLastStepError: boolean;
};
type RemoveAddOnType = { addOnProductInstanceId: string; productName: string; notes?: string };
type ErrorPayload = RuntimeError | ErrorsObj | undefined | null;
export type CustomError = {
  operation: string;
  message: string[];
};

export type ModalType = ScrubAddressModalType | AddonManagementModalType | null;

export type AddOnsState = {
  loading: boolean;
  error: ErrorPayload;
  availableAddons: {
    loading: boolean;
    error: ErrorPayload;
    productTypes: Array<ProductType> | null;
  };
  currentShopAddonId: string | null;
  currentlySelectedAddOnName: string | null;
  currentAddOns: Array<GetCurrentAddOnsPayload> | null;
  availableNumbersLoading: boolean;
  availableNumbers: AvailablePhoneNumber[] | null;
  availableNumbersError: ErrorPayload;
  reserveNumberLoading: boolean;
  reserveNumberSuccess: boolean | null;
  reserveNumberError: ErrorPayload;
  configureAddon: PurchasedAddOn | null;
  configureAddonLoading: boolean;
  configureAddonSuccess: boolean | null;
  configureAddonError: ErrorPayload;
  addOnConsentRequirements: Record<string, boolean>;
  getEicMessageError: string | null | unknown;
  eicMessageLoading: boolean;
  eicMessage: EicMessage | null;
  eicMessageSuccess: boolean | null;
  isErrorDisclosureChecked: boolean;
  eicChecked: boolean;
  voiceConfig: VoiceConfig;
  modalType: ModalType;
  isScrubbingE911Loading: boolean;
  scrubbedE911Address: ScrubbedE911Address;
  errorE911: ErrorPayload | CustomError;
  voiceEquipmentError: boolean | null;
  submitOrder: {
    loading: boolean;
    error: unknown | string | null;
    success: boolean;
  };
  addOnOrderId: string | null;
  selectedEasyCareProductId: string | null;
  isMissingEasyCareSelection: boolean | null;
  scrollToConsent: boolean;
};

export const initialAddOnsState: AddOnsState = {
  loading: true,
  error: null,
  availableAddons: {
    loading: false,
    error: null,
    productTypes: null,
  },
  currentShopAddonId: null,
  currentAddOns: null,
  currentlySelectedAddOnName: null,
  availableNumbersLoading: false,
  availableNumbers: null,
  availableNumbersError: null,
  reserveNumberLoading: false,
  reserveNumberSuccess: null,
  reserveNumberError: null,
  configureAddon: null,
  configureAddonLoading: false,
  configureAddonSuccess: null,
  configureAddonError: null,
  addOnConsentRequirements: {},
  getEicMessageError: null,
  eicMessageSuccess: null,
  eicMessage: null,
  eicMessageLoading: false,
  voiceConfig: {
    postalCode: '',
    selectedPhoneNumber: '',
    emergencyAddress: {
      streetAddress: '',
      aptUnit: '',
      cityAddress: '',
      stateProvince: '',
      zipCode: '',
    },
    useServiceAddressForE911: true,
    callerId: '',
    blockCallerId: false,
    directoryListingType: null,
    publishDirectoryListAddress: false,
    showLastStepError: false,
  },
  modalType: null,
  isScrubbingE911Loading: false,
  scrubbedE911Address: {
    rawAddress: {
      addressLines: [],
      countryCode: '',
      municipality: '',
      postalCode: '',
      region: '',
    },
    formattedAddress: { formattedAddressLine1: '', formattedAddressLine2: '' },
    processStatus: undefined,
    recommendation: undefined,
  },
  errorE911: null,
  voiceEquipmentError: null,
  submitOrder: {
    loading: false,
    error: null,
    success: false,
  },
  addOnOrderId: null,
  selectedEasyCareProductId: null,
  isMissingEasyCareSelection: false,
  eicChecked: false,
  isErrorDisclosureChecked: false,
  scrollToConsent: false,
};

export type PurchasedAddOn = {
  productName?: string;
  productTypeId: string;
  rootProductTypeId: string;
  shoppingCartId: string;
  voice?: string;
};

export const addOnsSlice = createSlice({
  name: 'addOns',
  initialState: initialAddOnsState,
  reducers: {
    // ------------------- Purchase add-on offer -------------------
    setCurrentShopAddonId: (state, action: PayloadAction<string | null>) => {
      state.currentShopAddonId = action.payload;
    },
    getAddOnOffers: (state) => {
      state.availableAddons.loading = true;
    },
    refetchAddOnOffers: (state) => {
      state.availableAddons.loading = true;
      state.availableAddons.error = null;
      state.addOnOrderId = null;
      state.submitOrder.error = null;
      state.submitOrder.success = false;
      state.voiceEquipmentError = null;
      state.configureAddonError = null;
      state.configureAddon = null;
    },
    setAddOnOffers: (state, action: PayloadAction<Array<ProductType>>) => {
      state.availableAddons.loading = false;
      state.availableAddons.productTypes = action.payload;
    },
    setAddOnOffersError: (state, action: PayloadAction<ErrorPayload>) => {
      state.availableAddons.loading = false;
      state.availableAddons.error = action.payload;
    },
    setConfigureAddonSuccess: (state, { payload }: { payload: boolean }) => {
      state.configureAddonLoading = false;
      state.configureAddonSuccess = payload;
    },
    setConfigureAddonError: (state, action: PayloadAction<ErrorPayload>) => {
      state.configureAddonLoading = false;
      state.configureAddonError = action.payload;
      state.configureAddonSuccess = false;
    },
    setEicRequired(state, action: PayloadAction<{ id: string; eicRequired: boolean }>) {
      const { id, eicRequired } = action.payload;
      state.addOnConsentRequirements = {
        ...state.addOnConsentRequirements,
        [id]: eicRequired,
      };
    },
    purchaseAddOn: (state, action: PayloadAction<PurchasedAddOn>) => {
      state.submitOrder.loading = true;
    },
    configureAddon: (state, action: PayloadAction<PurchasedAddOn>) => {
      state.configureAddonLoading = true;
      state.configureAddon = action.payload;
    },
    setPurchaseAddOnSuccess: (state, action: PayloadAction<boolean>) => {
      state.submitOrder.loading = false;
      state.submitOrder.error = null;
      state.submitOrder.success = action.payload;
    },
    setPurchaseAddOnError: (state, action: PayloadAction<unknown | string>) => {
      state.submitOrder.loading = false;
      state.submitOrder.error = action.payload;
    },
    setVoiceEquipmentError: (state, { payload }: { payload: boolean }) => {
      state.configureAddonLoading = false;
      state.submitOrder.loading = false;
      state.voiceEquipmentError = payload;
    },
    setAddOnOrderId(state, action: PayloadAction<string | null>) {
      state.addOnOrderId = action.payload;
    },

    // ------------------- Voice add-on -------------------
    getAvailableNumbers: (state, action: PayloadAction<string>) => {
      state.availableNumbersLoading = true;
      state.availableNumbersError = null;
    },
    setAvailableNumbers: (state, action: PayloadAction<AvailablePhoneNumber[] | null>) => {
      state.availableNumbers = action.payload;
      state.availableNumbersLoading = false;
    },
    setAvailableNumbersError: (state, action: PayloadAction<ErrorPayload>) => {
      state.availableNumbersLoading = false;
      state.availableNumbersError = action.payload;
    },
    reserveSelectedNumber: (state, action: PayloadAction<string>) => {
      state.voiceConfig.selectedPhoneNumber = action.payload;
      state.reserveNumberLoading = true;
    },
    setNumberReservationSuccess: (state) => {
      state.reserveNumberLoading = false;
      state.reserveNumberSuccess = true;
    },
    setNumberReservationError: (state, action: PayloadAction<ErrorPayload>) => {
      state.reserveNumberSuccess = false;
      state.reserveNumberLoading = false;
      state.reserveNumberError = action.payload;
    },
    setVoiceFormValues: (
      state,
      action: PayloadAction<Partial<VoiceConfig> | { emergencyAddress: Partial<EmergencyAddress> }>
    ) => {
      state.voiceConfig = {
        ...state.voiceConfig,
        ...action.payload,
        emergencyAddress: {
          ...state.voiceConfig.emergencyAddress,
          ...action.payload.emergencyAddress,
        },
      };
    },
    setShowLastStepError: (state, action: PayloadAction<boolean>) => {
      state.voiceConfig.showLastStepError = action.payload;
    },
    scrubEmergencyAddress: (state, _action: PayloadAction<Address>) => {
      state.isScrubbingE911Loading = true;
    },
    setShowModal: (state, action: PayloadAction<ModalType>) => {
      state.modalType = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setE911ScrubbedAddress: (state, action: PayloadAction<Pick<AddOnsState, 'scrubbedE911Address'>>) => {
      state.isScrubbingE911Loading = false;
      state.scrubbedE911Address = action.payload.scrubbedE911Address;
    },
    setE911Error: (state, action: PayloadAction<ErrorPayload | CustomError>) => {
      state.isScrubbingE911Loading = false;
      state.errorE911 = action.payload;
    },
    resetE911ScrubbedAddress: (state) => {
      return {
        ...state,
        scrubbedE911Address: initialAddOnsState.scrubbedE911Address,
      };
    },
    // ------------------- Current add-ons -------------------
    getCurrentAddOns: (state) => {
      state.loading = true;
      state.error = null;
    },
    setCurrentAddOns: (state, action: PayloadAction<GetCurrentAddOnsPayload[]>) => {
      state.loading = false;
      state.currentAddOns = action.payload;
      state.error = null;
    },
    setError: (state, action: PayloadAction<ErrorPayload>) => {
      state.loading = false;
      state.error = action.payload;
    },
    refetchCurrentAddOns: (state, action: PayloadAction<string>) => {
      state.submitOrder.success = false;
      state.loading = true;
      state.error = null;
    },
    removeAddOn: (state, action: PayloadAction<RemoveAddOnType>) => {
      state.loading = true;
      state.error = null;
    },
    setSelectedEasyCareProductId: (state, { payload }: PayloadAction<string>) => {
      state.selectedEasyCareProductId = payload;
      state.isMissingEasyCareSelection = false;
    },
    resetEasyCareSelection: (state) => {
      state.selectedEasyCareProductId = null;
    },
    setMissingSelection: (state, action: PayloadAction<boolean>) => {
      state.isMissingEasyCareSelection = action.payload;
    },
    // ------------------- Disclosures add-ons -------------------
    setEicMessage: (state, action: PayloadAction<any>) => {
      state.eicMessageLoading = false;
      state.eicMessage = action.payload;
    },
    getEicMessage: (state, action: PayloadAction<any>) => {
      state.eicMessageLoading = true;
      state.getEicMessageError = false;
    },
    setEicMessageError: (state, action: PayloadAction<string | unknown>) => {
      state.eicMessageLoading = false;
      state.getEicMessageError = action.payload;
    },
    setEicMessageSuccess: (state, { payload }: { payload: boolean }) => {
      state.eicMessageSuccess = payload;
    },
    setDisclosureChecked: (state, action: PayloadAction<boolean>) => {
      state.eicChecked = action.payload;
      state.isErrorDisclosureChecked = false;
    },
    setErrorDisclosureCheck: (state, action: PayloadAction<boolean>) => {
      state.isErrorDisclosureChecked = action.payload;
    },
    setScrollToConsent: (state, action: PayloadAction<boolean>) => {
      state.scrollToConsent = action.payload;
    },
    resetAddonConfiguration: (state) => {
      state.configureAddonSuccess = false;
      state.eicMessage = null;
      state.eicMessageSuccess = false;
      state.getEicMessageError = null;
    },
  },
});

export const {
  setError,
  setCurrentShopAddonId,
  setVoiceFormValues,
  setShowLastStepError,
  getAvailableNumbers,
  setAvailableNumbers,
  setAvailableNumbersError,
  reserveSelectedNumber,
  setNumberReservationSuccess,
  setNumberReservationError,
  scrubEmergencyAddress,
  setShowModal,
  setE911ScrubbedAddress,
  setE911Error,
  resetE911ScrubbedAddress,
  setConfigureAddonError,
  setConfigureAddonSuccess,
  setEicRequired,
  purchaseAddOn,
  configureAddon,
  setEicMessage,
  setPurchaseAddOnSuccess,
  setPurchaseAddOnError,
  getAddOnOffers,
  refetchAddOnOffers,
  setAddOnOffers,
  setAddOnOffersError,
  setAddOnOrderId,
  getCurrentAddOns,
  setCurrentAddOns,
  refetchCurrentAddOns,
  removeAddOn,
  setLoading,
  setVoiceEquipmentError,
  setSelectedEasyCareProductId,
  resetEasyCareSelection,
  setMissingSelection,
  getEicMessage,
  setEicMessageError,
  setEicMessageSuccess,
  setDisclosureChecked,
  setErrorDisclosureCheck,
  setScrollToConsent,
  resetAddonConfiguration,
} = addOnsSlice.actions;

export const selectAddOns = (state: { addOns: AddOnsState }) => state.addOns;

export const selectCurrentShopAddon = (state: { addOns: AddOnsState }) => {
  const { currentShopAddonId } = state.addOns;

  const currentShopAddon = state.addOns.availableAddons.productTypes?.find((addon) => addon.id === currentShopAddonId);

  if (!currentShopAddon) {
    return null;
  }

  return currentShopAddon;
};

export const selectCurrentEasyCareProduct = (state: { addOns: AddOnsState }) => {
  const { selectedEasyCareProductId } = state.addOns;
  const { currentShopAddonId } = state.addOns;

  const rootProduct = state.addOns.availableAddons.productTypes?.find((addon) => addon.id === currentShopAddonId);

  if (!rootProduct?.is_grouped_product) {
    return null;
  }

  const selectedEasyCareProduct = rootProduct?.package_types?.[0]?.product_types?.find((product) => {
    if (!product) {
      return null;
    }
    return product.id === selectedEasyCareProductId;
  });

  if (!selectedEasyCareProduct) {
    return null;
  }

  return selectedEasyCareProduct;
};

export const selectSubscribedEasyCare = (state: { addOns: AddOnsState }) => {
  const easyCareAddon = state.addOns.currentAddOns?.find((addon) => addon.kind === ADDON_KIND.EASY_CARE);

  if (!easyCareAddon) {
    return null;
  }

  return easyCareAddon;
};

export const selectCurrentAddons = (state: { addOns: AddOnsState }) => state.addOns.currentAddOns ?? [];

export const selectHasAddons = (state: { addOns: AddOnsState }) => Boolean(state.addOns.currentAddOns?.length);
